import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsPointingOutIcon, ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ApresentacaoViewerProps {
  url: string;
  titulo: string;
  versao: number;
  onFechar: () => void;
  /** Ação extra na barra (ex.: "Registrar esta sessão", só pro SI). */
  acaoExtra?: { label: string; onClick: () => void };
}

/**
 * Apresentação HTML (ex.: onboarding de SI) aberta dentro do SI, ocupando a tela. O arquivo roda
 * num iframe isolado (sandbox sem allow-same-origin: o script dele não alcança o login nem os
 * dados do Digital), com tela cheia e teclado funcionando. A barra some sozinha depois de uns
 * segundos parado — pra não aparecer no compartilhamento de tela — e volta com o mouse.
 */
export default function ApresentacaoViewer({ url, titulo, versao, onFechar, acaoExtra }: ApresentacaoViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [barraVisivel, setBarraVisivel] = useState(true);
  const timer = useRef<number | undefined>(undefined);

  const mostrarBarra = useCallback(() => {
    setBarraVisivel(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setBarraVisivel(false), 3000);
  }, []);

  useEffect(() => {
    mostrarBarra();
    const fecharNoEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.fullscreenElement) onFechar();
    };
    window.addEventListener('keydown', fecharNoEsc);
    const travaRolagem = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer.current);
      window.removeEventListener('keydown', fecharNoEsc);
      document.body.style.overflow = travaRolagem;
    };
  }, [mostrarBarra, onFechar]);

  const telaCheia = () => {
    const alvo = iframeRef.current?.parentElement;
    if (!document.fullscreenElement) alvo?.requestFullscreen?.().catch(() => undefined);
    else document.exitFullscreen().catch(() => undefined);
    iframeRef.current?.focus();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Apresentação: ${titulo}`}
      className="fixed inset-0 z-[70] flex flex-col bg-[#05060f]"
      onMouseMove={mostrarBarra}
    >
      <div
        className={`absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 border-b border-white/10 bg-[#0f1226]/95 px-3 py-2 text-gray-100 transition-opacity duration-300 ${
          barraVisivel ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onMouseEnter={mostrarBarra}
      >
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="Voltar aos Documentos"
          >
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <span className="truncate font-semibold">{titulo}</span>
          <span className="text-xs text-gray-400">v{versao}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-gray-400 md:inline">← → navegar · F tela cheia · N notas</span>
          {acaoExtra && (
            <button
              type="button"
              onClick={acaoExtra.onClick}
              className="rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              {acaoExtra.label}
            </button>
          )}
          <button
            type="button"
            onClick={telaCheia}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 hover:bg-white/10"
            aria-label="Tela cheia"
          >
            <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="h-full w-full bg-[#05060f]">
        <iframe
          ref={iframeRef}
          src={url}
          title={titulo}
          className="h-full w-full border-0"
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          allow="fullscreen"
          allowFullScreen
          onLoad={() => iframeRef.current?.focus()}
        />
      </div>
    </div>,
    document.body
  );
}
